import { useMemo, useState } from "react";
import { motion } from "motion/react";
import { ArrowRight, Loader2, Pencil } from "lucide-react";

import { type Message } from "shared/apiClient";
import { MessageType } from "shared/types";
import { getText } from "shared/lib";
import { animationVariants } from "../lib";

import { createMessageEvent, useChannel } from "shared/channel";
import {
  CaptureEventType,
  useAccount,
  useCaptureEvent,
  useNoticeboardData,
} from "@/hooks";

import { Button } from "shared/components";
import { QuestionLoader } from "@/components/wizard/questionLoader";
import {
  EditProposalSummaryDialog,
  ReviewProposal,
} from "@/components/reviewProposal";

export type ReviewProposalQuestionData = {
  answer_prefix?: string | null;
  expert_to_render: {
    output_key?: string;
    expert_name: string;
  };
  next_button_label?: string | null;
};

export interface ReviewProposalStepProps {
  disabled?: boolean;
  message: Message;
  showLoading?: boolean;
}

export function ReviewProposalStep({
  disabled,
  message,
  showLoading,
}: ReviewProposalStepProps) {
  const { captureEvent } = useCaptureEvent();

  const questionData = message.question_data as ReviewProposalQuestionData;

  const { channel } = useChannel();

  const { conversation } = useAccount();
  const { noticeboardData } = useNoticeboardData();

  const [submitting, setSubmitting] = useState(false);
  const [editProposalDialogOpen, setEditProposalDialogOpen] = useState(false);

  const content = useMemo(() => {
    const { expert_name = "", output_key = "" } =
      questionData?.expert_to_render || {};

    let content = "";
    if (noticeboardData?.[expert_name]?.parsed_output?.[output_key]) {
      content = noticeboardData[expert_name].parsed_output[output_key];
    } else if (noticeboardData?.[expert_name]?.raw_output) {
      content = noticeboardData[expert_name].raw_output;
    }

    return content?.replace(/^<.*>/, "").replace(/^<\/.*>/, "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickSubmitUpdatedProposal = (text: string) => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        message_type: MessageType.REVIEW_PROPOSAL,
        text,
      });

      console.log(
        "<ReviewProposalStep> handleClickSubmitUpdatedProposal()",
        messageDescriptor,
      );
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  const handleClickAcceptAndContinue = () => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        message_type: MessageType.REVIEW_PROPOSAL,
        text: getText("I do not want to make any amendments to the proposal."),
      });

      console.log(
        "<ReviewProposalStep> handleClickAcceptAndContinue()",
        messageDescriptor,
      );
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  const renderFooter = () => {
    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="mt-3 flex justify-end gap-2.5 md:mt-8"
      >
        <Button
          data-testid="edit-button"
          disabled={submitting || disabled}
          onClick={() => setEditProposalDialogOpen(true)}
          size="sm"
          variant="outline"
        >
          {getText("Edit")}
          <Pencil className="max-h-3 max-w-3" />
        </Button>

        <Button
          data-testid="next-button"
          disabled={submitting || disabled}
          onClick={handleClickAcceptAndContinue}
          size="sm"
        >
          {submitting || showLoading ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {getText(questionData.next_button_label || "Accept and continue")}
          <ArrowRight />
        </Button>
      </motion.div>
    );
  };

  if (showLoading) {
    return <QuestionLoader />;
  }

  return (
    <div
      className="wizard-step-transition flex h-full w-full flex-col text-bodylg md:max-w-[600px]"
      data-testid="step-container-review-proposal"
    >
      <ReviewProposal content={content} />

      {content ? (
        <EditProposalSummaryDialog
          content={content}
          onOpenChange={setEditProposalDialogOpen}
          onSubmit={handleClickSubmitUpdatedProposal}
          open={editProposalDialogOpen}
          submitting={submitting}
        />
      ) : null}

      {renderFooter()}
    </div>
  );
}
